import { useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from '../utils/api';

/**
 * Hook to fetch status events from /v1/status
 */
export function useStatusData(mac, interval = 'today', customRange = { start: '', end: '' }) {
    const [statusEvents, setStatusEvents] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Keep track if component is mounted to avoid setting state on unmounted component
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const fetchData = useCallback(async () => {
        if (!mac) {
            setStatusEvents([]);
            setMeta(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = {};
            if (interval === 'custom') {
                params.dt_start = customRange.start;
                params.dt_end = customRange.end;
            }

            const data = await apiClient.getStatusEvents(mac, interval, params);

            if (mountedRef.current) {
                setStatusEvents(data.items || []);
                setMeta(data.meta || null);
                setLastUpdated(new Date());
            }
        } catch (err) {
            if (mountedRef.current) {
                console.error("Failed to fetch status events", err);
                setError(err.message || 'Failed to load status events');
            }
        } finally {
            if (mountedRef.current) {
                setLoading(false);
            }
        }
    }, [mac, interval, customRange.start, customRange.end]);

    // Initial fetch when deps change
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        statusEvents,
        meta,
        loading,
        error,
        lastUpdated,
        refresh: fetchData
    };
}
